import React, {
    useMemo,
    useState,
} from "react";

import {
    useAutoplaySequence,
} from "../../../../../presentation/public-site/use-autoplay-sequence";
import {
    platformSecurityLevels,
} from "../../content/platform-security";
import LandingCheckIcon from "../../ui/LandingCheckIcon";

const SECURITY_AUTOPLAY_INTERVAL_MS =
    3000;

const SECURITY_LEVEL_NUMBERS =
    platformSecurityLevels.map(
        ({number}) => number
    );

function ShieldIcon() {
    return (
        <svg
            width="28"
            height="28"
            viewBox="0 0 28 28"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
        >
            <path
                d="M14 3.5L22.1667 6.41667V12.25C22.1667 17.3717 18.6867 22.1083 14 23.9167C9.31333 22.1083 5.83333 17.3717 5.83333 12.25V6.41667L14 3.5Z"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinejoin="round"
            />

            <path
                d="M10.5 13.125L12.8333 15.4583L17.7917 10.5"
                stroke="currentColor"
                strokeWidth="1.8"
                strokeLinecap="round"
                strokeLinejoin="round"
            />
        </svg>
    );
}

export default function SecurityCapabilitiesCard({
    t,
}) {
    const [
        activeSecurityNumber,
        setActiveSecurityNumber,
    ] = useState(
        platformSecurityLevels[0].number
    );

    const {
        targetRef: securityCardRef,
        selectItem: selectSecurityLevel,
        stopAutoplay:
            stopSecurityAutoplay,
    } = useAutoplaySequence({
        itemIds:
            SECURITY_LEVEL_NUMBERS,
        activeItemId:
            activeSecurityNumber,
        setActiveItemId:
            setActiveSecurityNumber,
        intervalMs:
            SECURITY_AUTOPLAY_INTERVAL_MS,
        threshold: 0.35,
        rootMargin:
            "0px 0px -10% 0px",
    });

    const activeSecurityLevel =
        useMemo(
            () =>
                platformSecurityLevels.find(
                    (level) =>
                        level.number ===
                        activeSecurityNumber
                ) ||
                platformSecurityLevels[0],
            [activeSecurityNumber]
        );

    return (
        <article
            className="capabilities-security"
            id="security"
            ref={securityCardRef}
        >
            <div className="capabilities-security__header">
                <div className="capabilities-security__icon">
                    <ShieldIcon/>
                </div>

                <div>
                    <span>
                        {t(
                            "main.landing.capabilities.security.eyebrow"
                        )}
                    </span>

                    <h3>
                        {t(
                            "main.landing.capabilities.security.title"
                        )}
                    </h3>
                </div>
            </div>

            <div
                className="capabilities-security__tabs"
                role="tablist"
                aria-label={t(
                    "main.landing.capabilities.security.tabsAriaLabel"
                )}
            >
                {platformSecurityLevels.map(
                    (level) => {
                        const active =
                            level.number ===
                            activeSecurityNumber;

                        const tabId =
                            `capabilities-security-tab-${level.number}`;

                        return (
                            <button
                                id={
                                    tabId
                                }
                                className={`capabilities-security__tab${
                                    active
                                        ? " capabilities-security__tab--active"
                                        : ""
                                }`}
                                type="button"
                                role="tab"
                                aria-selected={
                                    active
                                }
                                aria-controls="capabilities-security-panel"
                                aria-label={`${level.number}. ${t(
                                    level.titleKey
                                )}`}
                                onClick={() =>
                                    selectSecurityLevel(
                                        level.number
                                    )
                                }
                                onFocus={
                                    stopSecurityAutoplay
                                }
                                key={
                                    level.number
                                }
                            >
                                <strong>
                                    {
                                        level.number
                                    }
                                </strong>

                                <span>
                                    {t(
                                        level.shortTitleKey
                                    )}
                                </span>
                            </button>
                        );
                    }
                )}
            </div>

            <div
                id="capabilities-security-panel"
                className="capabilities-security__content"
                role="tabpanel"
                aria-labelledby={
                    `capabilities-security-tab-${activeSecurityLevel.number}`
                }
                key={
                    activeSecurityLevel.number
                }
            >
                <span className="capabilities-security__number">
                    {t(
                        "main.landing.capabilities.security.levelLabel",
                        {
                            number:
                                activeSecurityLevel.number,
                        }
                    )}
                </span>

                <h4>
                    {t(
                        activeSecurityLevel.titleKey
                    )}
                </h4>

                <p>
                    {t(
                        activeSecurityLevel.descriptionKey
                    )}
                </p>
            </div>

            <div className="capabilities-security__result">
                <span className="capabilities-security__result-icon">
                    <LandingCheckIcon/>
                </span>

                <span>
                    {t(
                        "main.landing.capabilities.security.result"
                    )}
                </span>
            </div>
        </article>
    );
}
